import api from "../../../api/api";
import { getErrorMessage, readApiError, type ApiError } from "../../../api/apiError";
import { duplicateTemplateNameMessage, ErrorMessages } from "../../../constants/messages";
import { prepareSaveContent } from "../../save";
import { getColumnLetter, id } from "../../utils";
import type { TemplateWorkbookConfig } from "../TemplateWorkbookService";

export interface TemplateSaveResult {
  success: boolean;
  templateId?: string;
  error?: string;
  /** HTTP status of the failed save, so callers can react to 401 separately from a 400. */
  status?: number;
}

type TemplateSaveResponse = {
  templateId?: string;
  TemplateId?: string;
};

const TEMPLATE_ID_PROPERTY_NAME = "BuilderMetrix.TemplateId";

type DataWorksheetInfo = {
  sheet: Excel.Worksheet;
  sheetName: string;
  dataNameId: string;
  columnCount: number;
  headerValues: string[];
};

function isDataWorksheetName(sheetName: string): boolean {
  return sheetName === "(data)" || /^\(data-.+\)$/.test(sheetName);
}

function getDataNameId(sheetName: string): string {
  if (sheetName === "(data)") {
    return "data";
  }

  return id(sheetName.substring(1, sheetName.length - 1));
}

function getSheetFormulaName(sheetName: string): string {
  return sheetName.replace(/'/g, "''");
}

function getColumnRangeName(dataNameId: string, columnName: string, columnIndex: number): string {
  const sanitizedColumnName = columnName.replace(/\s+/g, "_").replace(/[^A-Za-z0-9_]/g, "");
  return `${dataNameId}_${sanitizedColumnName || `Column${columnIndex + 1}`}`;
}

function formulaReferencesDataName(formula: unknown, dataNameIds: string[]): formula is string {
  if (typeof formula !== "string" || !formula.startsWith("=")) {
    return false;
  }

  return dataNameIds.some((dataNameId) => {
    const escapedName = dataNameId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(^|[^A-Za-z0-9_])'?${escapedName}'?([^A-Za-z0-9_]|$)`, "i").test(formula.substring(1));
  });
}

async function refreshVisibleDataReferenceFormulas(
  context: Excel.RequestContext,
  sheets: Excel.WorksheetCollection,
  dataNameIds: string[]
): Promise<void> {
  for (const sheet of sheets.items) {
    sheet.load("name,visibility");
  }
  await context.sync();

  for (const sheet of sheets.items) {
    if (sheet.visibility !== Excel.SheetVisibility.visible || isDataWorksheetName(sheet.name)) {
      continue;
    }

    const usedRange = sheet.getUsedRangeOrNullObject();
    usedRange.load("rowCount,columnCount,formulas");
    await context.sync();

    if (usedRange.isNullObject) {
      continue;
    }

    const formulas = usedRange.formulas as unknown[][];
    for (let rowIndex = 0; rowIndex < usedRange.rowCount; rowIndex++) {
      for (let columnIndex = 0; columnIndex < usedRange.columnCount; columnIndex++) {
        const formula = formulas[rowIndex]?.[columnIndex];
        if (formulaReferencesDataName(formula, dataNameIds)) {
          usedRange.getCell(rowIndex, columnIndex).formulas = [[formula]];
        }
      }
    }
  }
}

async function clearTemplateCachedData(): Promise<void> {
  await Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();

    const dataWorksheets = sheets.items.filter((sheet) => isDataWorksheetName(sheet.name));
    const dataWorksheetInfos: DataWorksheetInfo[] = [];

    for (const sheet of dataWorksheets) {
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load("rowCount,columnCount");
      await context.sync();

      if (usedRange.isNullObject) {
        continue;
      }

      try {
        sheet.protection.unprotect();
        await context.sync();
      } catch {
        // Continue when the sheet is already unprotected or the host rejects unprotect.
      }

      const columnCount = usedRange.columnCount;
  const headerRange = sheet.getRangeByIndexes(0, 0, 1, columnCount);
  headerRange.load("values");
  await context.sync();
  const headerValues = (headerRange.values[0] ?? []).map((value) => String(value ?? ""));

      if (usedRange.rowCount > 1 && columnCount > 0) {
        sheet.getRangeByIndexes(1, 0, usedRange.rowCount - 1, columnCount).clear(Excel.ClearApplyTo.contents);
        await context.sync();
      }

      if (columnCount > 0) {
        dataWorksheetInfos.push({
          sheet,
          sheetName: sheet.name,
          dataNameId: getDataNameId(sheet.name),
          columnCount,
          headerValues,
        });
      }
    }

    const names = context.workbook.names;
    names.load("items/name");
    const sampleName = names.getItemOrNullObject("sample");
    await context.sync();

    if (!sampleName.isNullObject) {
      try {
        sampleName.getRange().clear();
      } catch {
        // If the name no longer resolves to a range, still remove the marker below.
      }
      sampleName.delete();
    }

    for (const namedItem of names.items) {
      for (const info of dataWorksheetInfos) {
        if (namedItem.name.startsWith(`${info.dataNameId}_`)) {
          namedItem.delete();
          break;
        }
      }
    }
    await context.sync();

    for (const info of dataWorksheetInfos) {
      const escapedSheetName = getSheetFormulaName(info.sheetName);
      const dataRangeFormula = `='${escapedSheetName}'!$A$1:$${getColumnLetter(info.columnCount)}$1`;
      const dataName = context.workbook.names.getItemOrNullObject(info.dataNameId);
      await context.sync();

      if (dataName.isNullObject) {
        context.workbook.names.add(info.dataNameId, dataRangeFormula);
      } else {
        dataName.formula = dataRangeFormula;
      }

      for (let i = 0; i < info.columnCount; i++) {
        const columnLetter = getColumnLetter(i + 1);
        const columnRangeName = getColumnRangeName(info.dataNameId, info.headerValues[i], i);
        context.workbook.names.add(columnRangeName, `='${escapedSheetName}'!$${columnLetter}$1:$${columnLetter}$1`);
      }

      try {
        info.sheet.protection.protect();
        info.sheet.visibility = Excel.SheetVisibility.hidden;
      } catch {
        // Saving can proceed even if protection/visibility cannot be restored on a host.
      }
    }

    await refreshVisibleDataReferenceFormulas(
      context,
      sheets,
      dataWorksheetInfos.map((info) => info.dataNameId)
    );

    context.workbook.application.calculate(Excel.CalculationType.fullRebuild);

    for (const sheet of sheets.items) {
      sheet.pivotTables.load("items");
    }
    await context.sync();

    for (const sheet of sheets.items) {
      for (const pivot of sheet.pivotTables.items) {
        pivot.refresh();
      }
    }

    if (Office.context.platform !== Office.PlatformType.OfficeOnline) {
      try {
        context.workbook.dataConnections.refreshAll();
      } catch (error) {
        console.log("Could not refresh workbook data connections before template save:", error);
      }
    }

    await context.sync();
  });
}

export async function writeWorkbookTemplateId(templateId: string): Promise<void> {
  await Excel.run(async (context) => {
    context.workbook.properties.load("custom");
    await context.sync();

    const prop = context.workbook.properties.custom.getItemOrNullObject(TEMPLATE_ID_PROPERTY_NAME);
    prop.load("value");
    await context.sync();

    if (!templateId) {
      if (!prop.isNullObject) {
        prop.delete();
      }
      await context.sync();
      return;
    }

    if (prop.isNullObject) {
      context.workbook.properties.custom.add(TEMPLATE_ID_PROPERTY_NAME, templateId);
    } else {
      prop.value = templateId;
    }

    await context.sync();
  });
}

/**
 * Restates BuilderMetrix's duplicate-name conflict in the user's terms.
 *
 * `Core.Service` carries `UQ_Service_Name_TypeId`, so saving a template under a name that is
 * already taken fails that unique constraint. BMX maps it to a 409 whose message names the EF
 * entity and the column list — "BMXService already exists with the same value for Name, TypeId."
 * That is correct but unusable in a task pane, and it is the one case where BMX's own wording
 * needs restating. Every other error, including any other 409, is shown exactly as BMX reported it.
 */
function restateTemplateSaveError(error: ApiError, templateName: string): string {
  const isDuplicateName =
    error.status === 409 && /already exists with (the same value|duplicate values)/i.test(error.message);

  if (isDuplicateName) {
    return duplicateTemplateNameMessage(templateName);
  }

  return error.message;
}

export async function saveTemplateToServer(
  config: TemplateWorkbookConfig,
  roles: string[],
  authToken: string,
  identityProvider: string,
  updateTemplateId?: string | null
): Promise<TemplateSaveResult> {
  try {
    await clearTemplateCachedData();
    const content = await prepareSaveContent();
    const response = await api.post(
      "template",
      {
        templateName: config.templateName,
        roleIds: roles,
        content,
      },
      authToken,
      identityProvider,
      { templateId: updateTemplateId || null }
    );
    if (!response.ok) {
      const apiError = await readApiError(response);
      return {
        success: false,
        error: restateTemplateSaveError(apiError, config.templateName),
        status: apiError.status,
      };
    }

    const responseBody = await response.json().catch(() => null) as TemplateSaveResponse | null;
    const templateId = responseBody?.templateId || responseBody?.TemplateId;
    return { success: true, templateId };
  } catch (err) {
    return { success: false, error: getErrorMessage(err, ErrorMessages.TemplateSaveFailed) };
  }
}
